import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AddressElement, Elements, useElements } from '@stripe/react-stripe-js'
import type { Stripe, StripeElements } from '@stripe/stripe-js'
import {
  Alert,
  Button,
  Card,
  Heading,
  HeadingVariants,
  Skeleton,
  Text,
} from '@/ui'

import { TextColors, TypographyStyles } from '@/styles'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  parseErrorCodeFromCatch,
  parseErrorFromCatch,
} from '@/utils/errorHelpers'
import {
  fetchBillingInformation,
  updateBillingAddress,
} from '@/features/billing/payment-method/paymentMethodActions'
import { STRIPE_APPEARANCE } from '@/features/billing/payment-method/appearance'
import { getStripe } from '@/features/billing/payment-method/stripeClient'
import { addressDefaults } from '@/features/billing/payment-method/PaymentMethodForm'
import type { BillingInformation } from '@/features/billing/payment-method/payment-method.types'
import { BillingSubpageBreadcrumb } from './BillingSubpageBreadcrumb'
import { PLAN_AND_BILLING_PATH } from './paths'

type FetchStatus = 'loading' | 'loaded' | 'failed'

const LOADING_MESSAGE = 'Loading secure form…'
const TAX_LOCATION_INVALID_ERROR_CODE = 'tax_location_invalid'

const SAVE_ERROR = 'We couldn’t save your billing information. Try again.'

const saveErrorCopy = (error: unknown): string =>
  parseErrorCodeFromCatch(error) === TAX_LOCATION_INVALID_ERROR_CODE
    ? parseErrorFromCatch(error, SAVE_ERROR)
    : SAVE_ERROR

interface FormActionsProps {
  canSave: boolean
  saving: boolean
  onSave: (elements: StripeElements) => Promise<void>
  onCancel: () => void
}

const FormActions = ({
  canSave,
  saving,
  onSave,
  onCancel,
}: FormActionsProps): JSX.Element => {
  const elements = useElements()
  return (
    <div className="flex gap-3">
      {canSave && (
        <Button
          disabled={saving || !elements}
          onClick={() => {
            if (elements) void onSave(elements)
          }}
        >
          {saving ? 'Working on it…' : 'Save'}
        </Button>
      )}
      <Button variant="secondary" disabled={saving} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  )
}

export const BillingInformationPage = (): JSX.Element => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const cardRef = useRef<HTMLDivElement>(null)
  const focusAfterLoadRef = useRef(false)
  const savingRef = useRef(false)
  const [information, setInformation] = useState<BillingInformation | null>(
    null
  )
  // Elements takes a Stripe instance once and warns on any later change, so this is never cleared.
  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [status, setStatus] = useState<FetchStatus>('loading')
  const [addressReady, setAddressReady] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  const goToPlanAndBilling = (billingSaved?: boolean): void => {
    navigate(PLAN_AND_BILLING_PATH, {
      state: billingSaved ? { billingSaved: true } : undefined,
    })
  }

  const load = useCallback(async (): Promise<void> => {
    setStatus('loading')
    setAddressReady(false)
    try {
      const loadedInformation = await fetchBillingInformation()(dispatch)
      const loadedStripe = await getStripe(loadedInformation.publishableKey)
      if (!loadedStripe) {
        setStatus('failed')
        return
      }
      setInformation(loadedInformation)
      setStripe(loadedStripe)
      setStatus('loaded')
    } catch {
      setStatus('failed')
    }
  }, [dispatch])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (status === 'loading' || !focusAfterLoadRef.current) return
    focusAfterLoadRef.current = false
    cardRef.current?.focus()
  }, [status])

  const retry = (): void => {
    // Try again unmounts while loading, which would drop focus to <body>.
    focusAfterLoadRef.current = true
    void load()
  }

  const save = async (elements: StripeElements): Promise<void> => {
    // State lags a render behind, so a second click inside getValue() would start another save.
    if (savingRef.current) return
    savingRef.current = true
    try {
      await submitAddress(elements)
    } finally {
      savingRef.current = false
    }
  }

  const submitAddress = async (elements: StripeElements): Promise<void> => {
    const addressValue = await elements.getElement(AddressElement)?.getValue()
    if (!addressValue?.complete) return
    setSaving(true)
    setSaveError(null)
    try {
      await updateBillingAddress({
        name: addressValue.value.name || undefined,
        line1: addressValue.value.address.line1,
        line2: addressValue.value.address.line2,
        city: addressValue.value.address.city,
        state: addressValue.value.address.state,
        postalCode: addressValue.value.address.postal_code,
        country: addressValue.value.address.country,
      })(dispatch)
      goToPlanAndBilling(true)
    } catch (error) {
      setSaveError(saveErrorCopy(error))
    } finally {
      setSaving(false)
    }
  }

  const loaded = status === 'loaded' && information !== null
  const showSkeleton = status === 'loading' || (loaded && !addressReady)

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-6 p-8">
      <BillingSubpageBreadcrumb currentPage="Billing information" />
      <Card
        ref={cardRef}
        tabIndex={-1}
        className="p-8 outline-none"
        childrenClassName="flex flex-col gap-6"
      >
        <Heading variant={HeadingVariants.H2}>Billing information</Heading>
        <p role="status" className="sr-only">
          {showSkeleton ? LOADING_MESSAGE : ''}
        </p>
        {status === 'failed' && (
          <div className="flex flex-col gap-3">
            <Alert variant="error">
              We couldn’t load your billing information. Try again.
            </Alert>
            <Button variant="secondary" onClick={retry}>
              Try again
            </Button>
          </div>
        )}
        {showSkeleton && (
          <div className="flex flex-col gap-4" aria-hidden>
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            {loaded && (
              <Text
                variant={TypographyStyles.BODY_SM}
                className="text-neutral-500"
              >
                {LOADING_MESSAGE}
              </Text>
            )}
          </div>
        )}
        <Elements stripe={stripe} options={{ appearance: STRIPE_APPEARANCE }}>
          {loaded && (
            <div
              className={
                addressReady
                  ? 'flex flex-col gap-6'
                  : 'invisible flex h-0 flex-col gap-6 overflow-hidden'
              }
              aria-hidden={addressReady ? undefined : true}
              {...(addressReady ? {} : { inert: '' })}
            >
              <div className="flex flex-col gap-1">
                <Text variant={TypographyStyles.LABEL_SM}>
                  {information.email}
                </Text>
                <Text
                  variant={TypographyStyles.BODY_SM}
                  color={TextColors.MUTED}
                >
                  This is the email on your billing record.
                </Text>
              </div>
              <div className="flex flex-col gap-4">
                {saveError && <Alert variant="error">{saveError}</Alert>}
                <AddressElement
                  onReady={() => setAddressReady(true)}
                  onLoadError={() => setStatus('failed')}
                  options={{
                    mode: 'billing',
                    allowedCountries: ['US'],
                    defaultValues: addressDefaults(information.address),
                  }}
                />
              </div>
            </div>
          )}
          <FormActions
            canSave={loaded && addressReady}
            saving={saving}
            onSave={save}
            onCancel={() => goToPlanAndBilling()}
          />
        </Elements>
      </Card>
    </div>
  )
}
